// src/Pages/Estabilishment/HomeEstablishment/index.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  MenuItem,
  Select,
  // alias: o nome Tooltip já é usado pelo Tooltip dos gráficos (recharts)
  Tooltip as MuiTooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Cell as PieCell,
} from "recharts";
import {
  getEstablishmentDashboard,
  IDashboardData, IDashboardFilters, IRestDashboard,
} from "../../../Services/EstablishmentDashboard";
import { api } from "../../../Services/Api";
import {
  getSelectedEstablishment,
  setSelectedEstablishment,
} from "../../../Services/selectedEstablishment";
import { getMissions } from "../../../Services/Mission";
import type { IRestMissions, IMission, IMissionRewards } from "../../../Services/Mission";
import { useT } from "../../../i18n";
import type { TFunction } from "../../../i18n";

// ─── Auth Image ───────────────────────────────────────────────────────────────
const AuthImage: React.FC<{ photoId?: string | null; style?: React.CSSProperties; fallback: React.ReactNode }> = ({ photoId, style, fallback }) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!photoId) return;
    let url: string;
    api.get(`/pt_BR/midway_photo/${photoId}`, { responseType: "blob", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((res: any) => { url = URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => setSrc(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [photoId]); // eslint-disable-line
  if (!src) return <>{fallback}</>;
  return <img src={src} alt="" style={style} />;
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const ic = (p: React.ReactNode, s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>;
const IconStore   = () => ic(<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>, 20);
const IconPin     = () => ic(<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>);
const IconDown    = () => ic(<><polyline points="6 9 12 15 18 9"/></>);
const IconArrow   = () => ic(<><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>);
const IconDiamond = () => ic(<><polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"/></>);
const IconLock    = () => ic(<><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>);
const IconCheck   = () => ic(<><polyline points="20 6 9 17 4 12"/></>);
const IconSend    = () => ic(<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>);
const IconStar    = () => ic(<><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>);
const IconMail    = () => ic(<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>);
const IconWA      = () => ic(<><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></>);
const IconDL      = () => ic(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>);
const IconFlt     = () => ic(<><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></>);
const IconWarning = () => ic(<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>, 20);
const IconChevL   = () => ic(<><polyline points="15 18 9 12 15 6"/></>);
const IconChevR   = () => ic(<><polyline points="9 18 15 12 9 6"/></>);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const DONUT_COLORS = ["#00b6bc", "#F4743B", "#2C3D5E", "#F4C842"];
const MONTH_PT     = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const DAYS_PT      = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
const WEEK_FULL    = ["Domingo","Segunda-feira","Terça-feira","Quarta-feira","Quinta-feira","Sexta-feira","Sábado"];

const fmt = (d: Date) => `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
const toISO = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const fmtMins = (m?: number | null) => !m ? "—" : m < 60 ? `${m} min` : `${Math.floor(m/60)}h ${m%60}min`;

function useOutsideClick(ref: React.RefObject<HTMLElement>, cb: () => void) {
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) cb(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, cb]);
}

// ─── Mobile hook ──────────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= bp);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth <= bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return mobile;
}

// ─── Calendar Component ───────────────────────────────────────────────────────
const Calendar: React.FC<{
  start: Date | null; end: Date | null;
  onSelect: (d: Date) => void;
  year: number; month: number;
  onPrev: () => void; onNext: () => void;
}> = ({ start, end, onSelect, year, month, onPrev, onNext }) => {
  const t = useT();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const inRange = (d: number) => {
    if (!start || !end) return false;
    const dt = new Date(year, month, d);
    return dt > start && dt < end;
  };
  const isStart = (d: number) => start && start.toDateString() === new Date(year, month, d).toDateString();
  const isEnd   = (d: number) => end   && end.toDateString()   === new Date(year, month, d).toDateString();

  return (
    <div style={{ width: 260 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button onClick={onPrev} style={{ background: "none", border: "none", cursor: "pointer", color: "#00b6bc", padding: 4 }}><IconChevL /></button>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{t(MONTH_PT[month])} {year}</span>
        <button onClick={onNext} style={{ background: "none", border: "none", cursor: "pointer", color: "#00b6bc", padding: 4 }}><IconChevR /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 6 }}>
        {["D","S","T","Q","Q","S","S"].map((d, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 11, color: "#aaa", fontWeight: 600, padding: "4px 0" }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const isSt = isStart(d);
          const isEn = isEnd(d);
          const isIn = inRange(d);
          return (
            <button key={i} onClick={() => onSelect(new Date(year, month, d))} style={{
              border: "none", borderRadius: isSt || isEn ? 8 : (isIn ? 0 : 8),
              background: isSt || isEn ? "#00b6bc" : isIn ? "#e6f9fa" : "transparent",
              color: isSt || isEn ? "#fff" : "#333",
              fontWeight: isSt || isEn ? 700 : 400,
              fontSize: 13, padding: "6px 2px", cursor: "pointer", textAlign: "center",
            }}>{d}</button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Date Picker Modal ────────────────────────────────────────────────────────
const DatePickerModal: React.FC<{
  start: Date; end: Date;
  onApply: (s: Date, e: Date) => void;
  onClose: () => void;
}> = ({ start, end, onApply, onClose }) => {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState<Date[]>([start, end]);
  const [step, setStep] = useState<0|1>(0); // 0=select start, 1=select end
  const [viewY, setViewY] = useState(start.getFullYear());
  const [viewM, setViewM] = useState(start.getMonth());

  useOutsideClick(ref, onClose);

  const handleSelect = (d: Date) => {
    if (step === 0) { setSel([d, d]); setStep(1); }
    else {
      const [s] = sel;
      if (d < s) { setSel([d, s]); } else { setSel([s, d]); }
      setStep(0);
    }
  };

  const prevMonth = () => { if (viewM === 0) { setViewM(11); setViewY(y => y-1); } else setViewM(m => m-1); };
  const nextMonth = () => { if (viewM === 11) { setViewM(0); setViewY(y => y+1); } else setViewM(m => m+1); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.2)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div ref={ref} style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
        <div style={{ marginBottom: 12, fontSize: 13, color: "#888" }}>
          {step === 0 ? t("Selecione a data inicial") : t("Selecione a data final")}
        </div>
        <Calendar start={sel[0]} end={sel[1]} onSelect={handleSelect} year={viewY} month={viewM} onPrev={prevMonth} onNext={nextMonth} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: "9px 20px", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 13, color: "#555" }}>{t("Cancelar")}</button>
          <button onClick={() => onApply(sel[0], sel[1])} style={{ padding: "9px 20px", background: "#00b6bc", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff" }}>{t("Filtrar")}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Chart Filter Popover ─────────────────────────────────────────────────────
const ChartFilterPopover: React.FC<{
  selectedDay: number | null;
  selectedHours: number[];
  availableHours: number[];
  onDayChange: (d: number | null) => void;
  onHourToggle: (h: number) => void;
  onSelectAllHours: () => void;
  onClearHours: () => void;
  onApply: () => void;
  onClose: () => void;
}> = ({ selectedDay, selectedHours, availableHours, onDayChange, onHourToggle, onSelectAllHours, onClearHours, onApply, onClose }) => {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, onClose);

  return (
    <div ref={ref} style={{ position: "absolute", top: "100%", right: 0, zIndex: 200, background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.12)", minWidth: 220, marginTop: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 12 }}>{t("Filtrar")}</div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "#aaa", marginBottom: 6 }}>{t("Dia")}</div>
        <div style={{ position: "relative" }}>
          <select value={selectedDay ?? ""} onChange={e => onDayChange(e.target.value === "" ? null : Number(e.target.value))}
            style={{ width: "100%", padding: "8px 12px", border: "1.5px solid #e8e8e8", borderRadius: 8, fontSize: 13, color: "#333", appearance: "none", background: "#fff", cursor: "pointer" }}>
            <option value="">{t("Todos os dias")}</option>
            {WEEK_FULL.map((d, i) => <option key={i} value={i}>{t(d)}</option>)}
          </select>
          <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#aaa" }}><IconDown /></span>
        </div>
      </div>

      <div>
        <div style={{ fontSize: 11, color: "#aaa", marginBottom: 6 }}>{t("Horário")}</div>
        <div style={{ maxHeight: 180, overflowY: "auto", border: "1.5px solid #e8e8e8", borderRadius: 8, padding: 8 }}>
          <button onClick={onClearHours} style={{ background: "none", border: "none", color: "#00b6bc", fontSize: 12, cursor: "pointer", marginBottom: 6, padding: 0, display: "block" }}>{t("Limpar seleção")}</button>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#333", marginBottom: 4, cursor: "pointer" }}>
            <input type="checkbox" checked={selectedHours.length === 0} onChange={onSelectAllHours} />
            {t("Selecionar todos")}
          </label>
          {availableHours.map(h => (
            <label key={h} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#333", marginBottom: 4, cursor: "pointer" }}>
              <input type="checkbox" checked={selectedHours.includes(h)} onChange={() => onHourToggle(h)} />
              {String(h).padStart(2,"0")}:00
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <button onClick={onClose} style={{ padding: "7px 14px", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8, cursor: "pointer", fontSize: 12, color: "#555", fontWeight: 600 }}>{t("Cancelar")}</button>
        <button onClick={onApply} style={{ padding: "7px 14px", background: "#00b6bc", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, color: "#fff", fontWeight: 700 }}>{t("Filtrar")}</button>
      </div>
    </div>
  );
};

// ─── Interest Filter Popover ──────────────────────────────────────────────────
const InterestFilterPopover: React.FC<{
  interests: string[];
  selected: string[];
  onToggle: (name: string) => void;
  onApply: () => void;
  onClose: () => void;
}> = ({ interests, selected, onToggle, onApply, onClose }) => {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, onClose);

  return (
    <div ref={ref} style={{ position: "absolute", top: "100%", right: 0, zIndex: 200, background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.12)", minWidth: 220, marginTop: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 10 }}>{t("Filtrar interesses")}</div>
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        {interests.map(name => (
          <label key={name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#333", marginBottom: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={selected.includes(name)} onChange={() => onToggle(name)} />
            {name}
          </label>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <button onClick={onClose} style={{ padding: "7px 14px", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8, cursor: "pointer", fontSize: 12, color: "#555", fontWeight: 600 }}>{t("Cancelar")}</button>
        <button onClick={onApply} style={{ padding: "7px 14px", background: "#00b6bc", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, color: "#fff", fontWeight: 700 }}>{t("Salvar")}</button>
      </div>
    </div>
  );
};

// ─── Reusable sub-components ──────────────────────────────────────────────────
const Trend: React.FC<{ v: number }> = ({ v }) => (
  <span style={{ fontSize: 12, fontWeight: 600, color: v >= 0 ? "#10b981" : "#ef4444" }}>{v >= 0 ? "▲" : "▼"} {Math.abs(v)}%</span>
);

const StatBlock: React.FC<{ label: string; value: React.ReactNode; trend?: number }> = ({ label, value, trend }) => (
  <div style={{ minWidth: 130 }}>
    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{label}</div>
    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
      <span style={{ fontSize: 30, fontWeight: 700, color: "#111" }}>{value}</span>
      {trend !== undefined && <Trend v={trend} />}
    </div>
  </div>
);

const ProgBar: React.FC<{ label: string; value: number; max: number }> = ({ label, value, max }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
      <span style={{ fontSize: 12, color: "#333" }}>{label}</span>
      <span style={{ fontSize: 11, color: "#aaa" }}>{value}</span>
    </div>
    <div style={{ height: 6, background: "#e8e8e8", borderRadius: 999 }}>
      <div style={{ height: "100%", width: `${Math.round((value / Math.max(max, 1)) * 100)}%`, background: "#00b6bc", borderRadius: 999 }} />
    </div>
  </div>
);

/** Texto da recompensa a partir do tipo e do valor vindos da API. */
const descreveRecompensa = (m: IMission, t: TFunction): string => {
  const v = m.reward.value;
  switch (m.reward.type) {
    case "push_bonus":
      return t("{v} disparo(s) extra(s) de notificação", { v });
    case "discount_percent":
      return t("{v}% de desconto na próxima mensalidade", { v });
    case "free_banner":
      return t("{v} banner(s) sem custo", { v });
    case "badge":
      return t("Selo de destaque no app");
    default:
      return m.description ?? "";
  }
};

/**
 * Card de missão com progresso real.
 *
 * Antes os cards eram fixos no código, com metas e recompensas que não
 * correspondiam às regras. Agora vêm do endpoint de missões.
 */
const TaskCard: React.FC<{
  title: string;
  sub: string;
  /** Regra completa da missão — exibida no tooltip do ícone de ajuda */
  description?: string | null;
  done?: boolean;
  current?: number;
  target?: number;
  percent?: number;
}> = ({ title, sub, description, done, current, target, percent }) => {
  const t = useT();

  return (
    <div style={{ flex: 1, minWidth: 180, padding: "12px 14px", border: `1.5px solid ${done ? "#00b6bc" : "#f59e0b"}`, borderRadius: 12, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 5, maxWidth: "85%" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#111", lineHeight: 1.4 }}>{title}</span>

          {/* A regra completa não cabe no card; fica no tooltip para o título
              continuar curto e legível. */}
          {description && (
            <MuiTooltip
              title={description}
              arrow
              enterTouchDelay={0}
              leaveTouchDelay={4000}
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: 12,
                    lineHeight: 1.6,
                    padding: "10px 12px",
                    maxWidth: 260,
                    background: "#111",
                  },
                },
                arrow: { sx: { color: "#111" } },
              }}
            >
              <span
                // tabIndex torna o tooltip acessível por teclado, já que um
                // span comum não recebe foco
                tabIndex={0}
                aria-label={t("Regra da missão: {title}", { title })}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  border: "1px solid #ccc",
                  color: "#999",
                  fontSize: 9,
                  fontWeight: 700,
                  cursor: "help",
                  flexShrink: 0,
                }}
              >
                ?
              </span>
            </MuiTooltip>
          )}
        </span>

        <span style={{ color: done ? "#10b981" : "#f59e0b", flexShrink: 0 }}>{done ? <IconCheck /> : <IconLock />}</span>
      </div>

      <span style={{ fontSize: 11, color: "#aaa" }}>{sub}</span>

      {typeof percent === "number" && !done && (
        <div style={{ marginTop: 8 }}>
          <div style={{ height: 5, background: "#f0f0f0", borderRadius: 999, overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${percent}%`,
                background: "#f59e0b",
                borderRadius: 999,
                transition: "width .3s",
              }}
            />
          </div>
          <span style={{ fontSize: 10, color: "#bbb", marginTop: 3, display: "block" }}>
            {t("{current} de {target}", { current: current ?? "", target: target ?? "" })}
          </span>
        </div>
      )}
    </div>
  );
};

const UpgradeCard: React.FC<{ planName: string; desc: string; cta: string; onClick: () => void }> = ({ planName, desc, cta, onClick }) => (
  <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", padding: "16px 18px", marginBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
      <span style={{ color: "#00b6bc" }}><IconDiamond /></span>
      <span style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>{planName}</span>
    </div>
    <p style={{ fontSize: 12, color: "#888", margin: "0 0 10px", lineHeight: 1.5 }}>{desc}</p>
    <button onClick={onClick} style={{ background: "none", border: "none", color: "#00b6bc", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 4 }}>
      {cta} <IconArrow />
    </button>
  </div>
);

const IconBtn: React.FC<{ icon: React.ReactNode; onClick?: () => void }> = ({ icon, onClick }) => (
  <button onClick={onClick} style={{ width: 28, height: 28, border: "none", borderRadius: 7, background: "#e6f9fa", color: "#00b6bc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</button>
);

const SectionHdr: React.FC<{ title: string; onFilter?: () => void; filterOpen?: boolean }> = ({ title, onFilter, filterOpen }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111" }}>{title}</h3>
    {onFilter && (
      <div style={{ display: "flex", gap: 6 }}>
        <IconBtn icon={<IconDL />} />
        <IconBtn icon={<IconFlt />} onClick={onFilter} />
      </div>
    )}
  </div>
);

const card: React.CSSProperties = { background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", padding: "16px 18px", marginBottom: 14 };

// ─── Donut ────────────────────────────────────────────────────────────────────
const DonutCard: React.FC<{ title: string; data: { label: string; value: number; sub?: string }[]; onFilter?: () => void }> = ({ title, data, onFilter }) => {
  const t = useT();
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div style={card}>
      <SectionHdr title={title} onFilter={onFilter} />
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <PieChart width={90} height={90}>
          <Pie data={data} cx={40} cy={40} innerRadius={25} outerRadius={42} dataKey="value" stroke="none">
            {data.map((_, i) => <PieCell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
          </Pie>
        </PieChart>
        <div style={{ flex: 1 }}>
          {data.length === 0 ? (
            <span style={{ fontSize: 12, color: "#ccc" }}>{t("Sem dados")}</span>
          ) : data.slice(0, 4).map((d, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: DONUT_COLORS[i], flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: "#333" }}>{d.label}</span>
              </div>
              <span style={{ fontSize: 11, color: "#aaa" }}>{d.sub ?? (total > 0 ? `${Math.round((d.value/total)*100)}%` : "—")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Weekly Chart (mobile) ─────────────────────────────────────────────────────
// Figma mobile: gráfico horizontal com dias na esquerda e horas no eixo X
const { BarChart: RBarChart, Bar: RBar, XAxis: RXAxis, YAxis: RYAxis, Tooltip: RTooltip, Cell: RCell, ResponsiveContainer: RRC } =
  { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer };

const WeeklyChart: React.FC<{
  data: { label: string; count: number }[];
}> = ({ data }) => {
  const t = useT();

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", padding: "16px 18px", marginBottom: 14 }}>
      <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#111" }}>{t("Cliques na semana")}</h3>
      {data.length === 0 ? (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#ccc", fontSize: 13 }}>{t("Sem dados")}</div>
      ) : (
        <RRC width="100%" height={data.length * 36 + 30}>
          <RBarChart data={data} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 28 }}>
            <RXAxis type="number" tick={{ fontSize: 10, fill: "#aaa" }} tickFormatter={v => `${String(v).padStart(2,"0")}:00`} />
            <RYAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: "#888" }} width={28} />
            <RTooltip formatter={(v: any) => [v, t("check-ins")]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            <RBar dataKey="count" radius={[0, 4, 4, 0]} barSize={20}>
              {data.map((_, i) => <RCell key={i} fill={i % 2 === 0 ? "#00b6bc" : "#80d8db"} />)}
            </RBar>
          </RBarChart>
        </RRC>
      )}
    </div>
  );
};

// ─── Peak Chart ───────────────────────────────────────────────────────────────
const PeakChart: React.FC<{
  data: { label: string; count: number }[];
  onFilter: () => void;
  filterSlot?: React.ReactNode;
}> = ({ data, onFilter, filterSlot }) => {
  const t = useT();

  return (
    <div style={{ ...card, position: "relative" }}>
      <SectionHdr title={t("Horários de pico")} onFilter={onFilter} />
      {filterSlot}
      {data.length === 0 ? (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#ccc", fontSize: 13 }}>{t("Sem dados para o período selecionado")}</div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#aaa" }} />
            <YAxis tick={{ fontSize: 10, fill: "#aaa" }} />
            <Tooltip formatter={(v: any) => [v, t("check-ins")]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill={i % 2 === 0 ? "#00b6bc" : "#80d8db"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export const HomeEstablishment = () => {
  const t         = useT();
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();

  // Data
  const [data,    setData]    = useState<IDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  // Date range
  const now         = new Date();
  const firstOfMon  = new Date(now.getFullYear(), now.getMonth(), 1);
  const [startDate, setStartDate] = useState<Date>(firstOfMon);
  const [endDate,   setEndDate]   = useState<Date>(now);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Establishment selector
  const [selId, setSelId] = useState("");

  // Missões da gamificação — substituem os cards fixos da seção "Tarefas"
  const [missions, setMissions] = useState<IMission[]>([]);
  const [missionRewards, setMissionRewards] = useState<IMissionRewards | null>(null);

  // Chart filter
  const [showChartFilter, setShowChartFilter] = useState(false);
  const [filterDay,   setFilterDay]   = useState<number | null>(null);
  const [filterHours, setFilterHours] = useState<number[]>([]);

  // Interest filter
  const [showIntFilter,  setShowIntFilter]  = useState(false);
  const [selInterests,   setSelInterests]   = useState<string[]>([]);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback((filters: IDashboardFilters) => {
    setLoading(true);
    getEstablishmentDashboard(filters)
      .then((res: any) => {
        const { data: r } = res as IRestDashboard;

        // Sempre grava o que o backend REALMENTE usou.
        // Se pedimos um estabelecimento que não é mais da conta, ele devolve
        // outro — e persistir a resposta limpa o ID obsoleto, em vez de
        // deixá-lo preso no armazenamento.
        if (r.data?.selectedId) {
          setSelectedEstablishment(r.data.selectedId);
        }

        setData(r.data);
        setSelId(r.data.selectedId);

        // missões da unidade em foco; falha aqui não derruba o painel
        getMissions()
          .then((mr) => {
            const md = (mr as IRestMissions).data.data;
            setMissions(md.missions ?? []);
            setMissionRewards(md.rewards ?? null);
          })
          .catch(() => {});
        setSelInterests([]);
      })
      .catch(() => toast.error(t("Erro ao carregar dashboard.")))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    // Recupera o estabelecimento escolhido antes de sair da tela. Sem isso o
    // backend devolve o primeiro da lista e a seleção se perdia a cada volta.
    const salvo = getSelectedEstablishment();

    load({
      startDate: toISO(startDate),
      endDate:   toISO(endDate),
      ...(salvo ? { establishmentId: salvo } : {}),
    });
  }, []); // eslint-disable-line

  const reload = (sd: Date, ed: Date, estabId?: string) => {
    const id = estabId !== undefined ? estabId : (selId || undefined);
    load({ startDate: toISO(sd), endDate: toISO(ed), establishmentId: id });
  };

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleDateApply = (s: Date, e: Date) => {
    setStartDate(s); setEndDate(e); setShowDatePicker(false); reload(s, e);
  };

  const handleEstabChange = (id: string) => {
    setSelId(id);
    // guarda a escolha para as demais telas (Banners, Push) usarem o mesmo
    // estabelecimento — o estado local morreria na navegação
    setSelectedEstablishment(id);
    reload(startDate, endDate, id);
  };

  const handleHourToggle = (h: number) => {
    setFilterHours(prev => prev.includes(h) ? prev.filter(x => x !== h) : [...prev, h]);
  };

  const handleIntToggle = (name: string) => {
    setSelInterests(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]);
  };

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}>
      <CircularProgress style={{ color: "#00b6bc" }} />
    </div>
  );

  if (!data) return <div style={{ color: "#aaa", padding: 32 }}>{t("Nenhum dado disponível.")}</div>;

  const { planType, establishment, stats, support, establishments } = data;
  const isInsight           = planType === "insight";
  const isCconnection       = planType === "connection";

  // ── Degustação e análise de documentos ──────────────────────────────────
  const trialDaysLeft   = data.trialDaysLeft;
  const trialEndsAt     = data.trialEndsAt;
  const docsPending     = data.documentsStatus === "pending";
  const docsRejected    = data.documentsStatus === "rejected";

  // Só o Insight tem período gratuito (3 meses). Connection e os planos
  // Showcase (Pulse/Wave) são pagos desde o início.
  const warnBefore      = isInsight ? 15 : 0;
  const trialEndingSoon =
    data.trialActive &&
    trialDaysLeft !== null &&
    warnBefore > 0 &&
    trialDaysLeft <= warnBefore;

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso.replace(" ", "T")).toLocaleDateString("pt-BR") : "";
  // Pulse e Wave (antigo Showcase) — mesmas vantagens por ora
  const isPulse      = planType === "pulse";
  const isWave       = planType === "wave";
  const isShowcase   = isPulse || isWave;

  // Apply client-side filters
  const rawInterests = stats.topInterests ?? [];
  const allHours     = (stats.peakHours ?? []).map(h => h.hour);

  const filteredHours = (stats.peakHours ?? []).filter(h => {
    if (filterHours.length > 0 && !filterHours.includes(h.hour)) return false;
    return true;
  });

  const filteredInterests = selInterests.length > 0
    ? rawInterests.filter(i => selInterests.includes(i.name))
    : rawInterests;

  const maxInterest  = rawInterests.length > 0 ? rawInterests[0].count : 1;
  const topLanguages = stats.topLanguages ?? [];
  const maxLanguage  = topLanguages.length > 0 ? topLanguages[0].count : 1;
  const activeUsers  = stats.activeUsers ?? null;
  const activeMeets  = stats.activeMeets ?? null;

  const bestDays  = stats.bestDays ?? [];
  // filtra zeros, ordena do maior para menor, pega top 4
  const donutData = bestDays
    .filter(d => d.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 4)
    .map(d => ({ label: d.label, value: d.count }));

  const totalCheckins = stats.totalCheckins ?? 0;
  const returning     = stats.returningUsers ?? 0;
  const returnData    = totalCheckins > 0 ? [
    { label: t("Recorrentes"), value: returning },
    { label: t("Novos"),       value: Math.max(0, totalCheckins - returning) },
  ] : [];

  return (
    <div style={{ maxWidth: isMobile ? "100%" : 1100, padding: isMobile ? "0 0 32px" : undefined }}>

      {/* ── Chamada de engajamento ───────────────────────────────────────────
          Primeira coisa da página nos planos Insight e Connection: são os
          dois que vivem de check-in. Pulse e Wave compram veiculação de
          banner, não dependem de público no local, então não veem o aviso.

          Fixo de propósito — não tem botão de fechar: a mensagem é o que
          move o número que o resto do painel mostra. */}
      {(isInsight || isCconnection) && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            background: "#fce7f3",
            border: "1px solid #f9a8d4",
            borderLeft: "4px solid #db2777",
            borderRadius: 12,
            padding: isMobile ? "12px 14px" : "14px 18px",
            marginBottom: isMobile ? 12 : 20,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1.3 }} aria-hidden="true">
            📣
          </span>
          <p
            style={{
              margin: 0,
              fontSize: isMobile ? 13 : 14,
              fontWeight: 600,
              color: "#9d174d",
              lineHeight: 1.5,
            }}
          >
            {t(
              "Engaje seus clientes para que façam check-in no app da Pertalks e tenha dados relevantes do público que frequenta o seu estabelecimento!",
            )}
          </p>
        </div>
      )}

      {/* Establishment header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: isMobile ? 12 : 20 }}>
        <div style={{ width: isMobile ? 40 : 50, height: isMobile ? 40 : 50, borderRadius: 10, overflow: "hidden", background: "#f5f5f5", border: "1px solid #efefef", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <AuthImage photoId={establishment.image} style={{ width: "100%", height: "100%", objectFit: "cover" }} fallback={<span style={{ color: "#ccc" }}><IconStore /></span>} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 20, fontWeight: 700, color: "#111" }}>{establishment.name}</h2>
          {establishment.addressInGoogleFormat && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#888", marginTop: 2 }}>
              <IconPin /> {establishment.addressInGoogleFormat}
            </div>
          )}
        </div>
      </div>

      {/* Period + buttons */}
      <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16, flexDirection: isMobile ? "column" : "row" }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12, flexWrap: "wrap" }}>
          <div style={{ width: "100%" }}>
            <div style={{ fontSize: 12, color: "#888" }}>{t("Período:")}</div>
            <button onClick={() => setShowDatePicker(true)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: isMobile ? 14 : 13, color: "#00b6bc", fontWeight: 600, padding: 0 }}>
              {t("{inicio} a {fim}", { inicio: fmt(startDate), fim: fmt(endDate) })}
            </button>
          </div>
          {!isMobile && (
            <>
              <button style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", border: "none", borderRadius: 8, background: "#00b6bc", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                <IconDL /> {t("Exportar relatório")}
              </button>
              <div style={{ position: "relative" }}>
                <button onClick={() => setShowIntFilter(v => !v)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", border: "1.5px solid #00b6bc", borderRadius: 8, background: "#fff", color: "#00b6bc", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  <IconFlt /> {t("Filtrar")} {selInterests.length > 0 && `(${selInterests.length})`}
                </button>
                {showIntFilter && (
                  <InterestFilterPopover
                    interests={rawInterests.map(i => i.name)}
                    selected={selInterests}
                    onToggle={handleIntToggle}
                    onApply={() => setShowIntFilter(false)}
                    onClose={() => setShowIntFilter(false)}
                  />
                )}
              </div>
            </>
          )}
          {isMobile && (
            <div style={{ display: "flex", gap: 8 }}>
              <button style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 10px", border: "none", borderRadius: 8, background: "#00b6bc", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                <IconDL />
              </button>
              <button onClick={() => setShowIntFilter(v => !v)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 10px", border: "1.5px solid #00b6bc", borderRadius: 8, background: "#fff", color: "#00b6bc", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                <IconFlt />
              </button>
            </div>
          )}
        </div>
        {establishments.length > 1 && (
          <div style={{ minWidth: isMobile ? "100%" : 220 }}>
            {/* Título acima do campo — deixa claro que o seletor troca a
                unidade exibida em todo o painel */}
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontSize: 13,
                fontWeight: 700,
                color: "#111",
              }}
            >
              {t("Estabelecimentos")}
            </label>

            <Select
              value={selId}
              onChange={e => handleEstabChange(e.target.value)}
              size="small"
              style={{
                width: "100%",
                background: "#fff",
                fontSize: 13,
                // acompanha o arredondamento dos cards do painel
                borderRadius: 12,
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "12px",
                  borderColor: "#e8e8e8",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00b6bc",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00b6bc",
                },
              }}
            >
              {establishments.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
            </Select>
          </div>
        )}
      </div>

      {/* Date picker modal */}
      {showDatePicker && (
        <DatePickerModal start={startDate} end={endDate} onApply={handleDateApply} onClose={() => setShowDatePicker(false)} />
      )}

      {/* ── Documentos em análise ────────────────────────────────────────── */}
      {(docsPending || docsRejected) && (
        <div style={{
          ...card,
          display: "flex", alignItems: "flex-start", gap: 12,
          background: docsRejected ? "#fff7e6" : "#e6f9fa",
          border: `1px solid ${docsRejected ? "#fde68a" : "#cdf3f5"}`,
        }}>
          <span style={{ fontSize: 22, lineHeight: 1 }}>{docsRejected ? "⚠️" : "⏳"}</span>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#111" }}>
              {docsRejected
                ? t("Precisamos de um ajuste nos seus documentos")
                : t("Seus documentos estão em análise")}
            </p>
            <p style={{ margin: 0, fontSize: 13, color: "#666", lineHeight: 1.6 }}>
              {docsRejected
                ? t("Encontramos uma divergência. Verifique seu e-mail — nossa equipe explicou o que precisa ser reenviado.")
                : t("O prazo é de até 72 horas. Seu painel já está liberado normalmente.")}
              {isCconnection && " " + t("As áreas de Banners e Notificações Push abrem após a aprovação.")}
            </p>
          </div>
        </div>
      )}

      {/* ── Fim da degustação se aproximando ─────────────────────────────── */}
      {trialEndingSoon && (
        <div style={{
          ...card,
          display: "flex", alignItems: "flex-start", gap: 12,
          background: "#fff7e6", border: "1px solid #fde68a",
        }}>
          <span style={{ fontSize: 22, lineHeight: 1 }}>🔔</span>
          <div style={{ flex: 1 }}>
            <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#111" }}>
              {t("Seu período gratuito termina em {dias} dia(s)", { dias: trialDaysLeft ?? "" })}
            </p>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#666", lineHeight: 1.6 }}>
              {t("Cadastre um cartão até {data} para continuar sem interrupção (R$ 29,90/mês). Aproveite para conhecer o plano Connection, com push, banners e destaque no mapa.", { data: fmtDate(trialEndsAt) })}
            </p>
            <button
              onClick={() => navigate("/establishment/subscription")}
              style={{
                padding: "9px 20px", background: "#00b6bc", color: "#fff",
                border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
            >
              {t("Cadastrar cartão")}
            </button>
          </div>
        </div>
      )}

      {/* No plan */}
      {planType === "none" && (
        <div style={{ ...card, textAlign: "center", padding: "48px 24px" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 8px" }}>{t("Você ainda não possui um plano ativo")}</h3>
          <p style={{ fontSize: 13, color: "#aaa", margin: "0 0 20px" }}>{t("Assine um plano para visualizar os dados do seu estabelecimento.")}</p>
          <button onClick={() => navigate("/establishment/subscription")} style={{ padding: "11px 28px", background: "#00b6bc", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
            {t("Ver planos disponíveis")}
          </button>
        </div>
      )}

      {/* ══ SHOWCASE ═══════════════════════════════════════════════════════════
           Layout Figma: [gráfico cliques] [top 10 assuntos] [plano + campanhas]
           Exibe: Total de cliques, gráfico, Top 10 Assuntos,
                  Plano contratado (Pulse ou Wave), Campanhas anteriores
           Não exibe: donuts, push, tasks, insights, idiomas               */}
      {isShowcase && (
        <>
          {/* Stats */}
          <div style={{ ...card, display: "flex", gap: 32, flexWrap: "wrap" }}>
            <StatBlock label={t("Total de cliques")} value={stats.totalCheckins?.toLocaleString("pt-BR") ?? "—"} trend={10} />
          </div>

          {/* 3 colunas: gráfico | top assuntos | plano + campanhas */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 240px 240px", gap: 14, alignItems: "start" }}>

            {/* Col 1 — Gráfico */}
            <div style={{ position: "relative" }}>
              {isMobile
                ? <WeeklyChart data={bestDays.map(d => ({ label: d.label, count: d.count }))} />
                : <PeakChart data={filteredHours} onFilter={() => setShowChartFilter(v => !v)}
                filterSlot={showChartFilter && (
                  <ChartFilterPopover
                    selectedDay={filterDay} selectedHours={filterHours} availableHours={allHours}
                    onDayChange={setFilterDay} onHourToggle={handleHourToggle}
                    onSelectAllHours={() => setFilterHours([])} onClearHours={() => setFilterHours([])}
                    onApply={() => setShowChartFilter(false)} onClose={() => setShowChartFilter(false)}
                  />
                )}
              />}
            </div>

            {/* Col 2 — Top 10 Assuntos/Interesses */}
            <div style={{ ...card, position: "relative" }}>
              <SectionHdr title={t("Top 10 Assuntos/Interesses")} onFilter={() => setShowIntFilter(v => !v)} />
              {showIntFilter && (
                <InterestFilterPopover
                  interests={rawInterests.map(i => i.name)} selected={selInterests}
                  onToggle={handleIntToggle} onApply={() => setShowIntFilter(false)} onClose={() => setShowIntFilter(false)}
                />
              )}
              <p style={{ fontSize: 10, color: "#aaa", margin: "0 0 10px" }}>{t("Campanha atual")}</p>
              {filteredInterests.length === 0
                ? <p style={{ fontSize: 12, color: "#ccc", margin: 0 }}>{t("Sem dados")}</p>
                : filteredInterests.slice(0, 10).map(i => (
                  <div key={i.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <span style={{ color: "#00b6bc" }}><IconStar /></span>
                      <span style={{ fontSize: 12, color: "#333" }}>{i.name}</span>
                    </div>
                    <span style={{ fontSize: 11, color: "#aaa" }}>{t("{count} usuários", { count: i.count })}</span>
                  </div>
                ))
              }
            </div>

            {/* Col 3 — Plano atual (Pulse/Wave) + Campanhas anteriores + Suporte */}
            <div>
              <UpgradeCard
                planName={data.planName ?? (isPulse ? t("Plano Pulse") : t("Plano Wave"))}
                desc={t("Você tem acesso ao espaço premium de divulgação no app. Acompanhe o desempenho das suas campanhas.")}
                cta={t("Gerenciar assinatura")}
                onClick={() => navigate("/establishment/subscription")}
              />
              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111" }}>{t("Campanhas anteriores")}</h3>
                  <button style={{ background: "none", border: "none", cursor: "pointer", color: "#00b6bc" }}><IconArrow /></button>
                </div>
                <p style={{ fontSize: 12, color: "#ccc", textAlign: "center", padding: "8px 0", margin: 0 }}>{t("Nenhuma campanha anterior")}</p>
              </div>
              <div style={card}>
                <p style={{ fontSize: 11, fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: 1, margin: "0 0 10px" }}>{t("Suporte")}</p>
                <a href={`mailto:${support.email}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", background: "#f5f5f5", borderRadius: 8, color: "#333", textDecoration: "none", fontWeight: 600, fontSize: 12, border: "1px solid #e8e8e8" }}><IconMail /> {t("E-mail")}</a>
              </div>
            </div>

          </div>
        </>
      )}

            {/* ══ CONNECTION (Plano connection no Figma) ══════════════════════════════
           Layout Figma: [gráfico + donuts] [plano connection + idiomas] [assuntos]
           Exibe: check-ins, Horários de pico, Top 10 Idiomas,
                  Top 10 Assuntos, Melhores dias donut, Retorno donut
           Não exibe: push banner, tasks, insights do momento               */}
      {isInsight && !isCconnection && (
        <>
          {/* Stats */}
          <div style={{ ...card, display: "flex", gap: 40 }}>
            <StatBlock label={t("Total de check-ins")} value={stats.totalCheckins?.toLocaleString("pt-BR") ?? "—"} trend={10} />
          </div>

          {/* 3 colunas: gráfico+donuts | plano+idiomas | assuntos */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 240px 240px", gap: 14, alignItems: "start" }}>

            {/* Col 1 — Horários de pico + donuts */}
            <div>
              <div style={{ position: "relative" }}>
                {isMobile
                  ? <WeeklyChart data={bestDays.map(d => ({ label: d.label, count: d.count }))} />
                  : <PeakChart data={filteredHours} onFilter={() => setShowChartFilter(v => !v)}
                    filterSlot={showChartFilter && (
                      <ChartFilterPopover
                        selectedDay={filterDay} selectedHours={filterHours} availableHours={allHours}
                        onDayChange={setFilterDay} onHourToggle={handleHourToggle}
                        onSelectAllHours={() => setFilterHours([])} onClearHours={() => setFilterHours([])}
                        onApply={() => setShowChartFilter(false)} onClose={() => setShowChartFilter(false)}
                      />
                    )}
                  />}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                <DonutCard title={t("Melhores dias e horários")} data={donutData} onFilter={() => setShowDatePicker(true)} />
                <DonutCard title={t("Estatísticas de retorno do público")} data={returnData} onFilter={() => setShowDatePicker(true)} />
              </div>
            </div>

            {/* Col 2 — Oferta de upgrade para Connection + Top 10 Idiomas */}
            <div>
              {/* Este bloco só aparece para quem É Insight, então o card
                  oferece o plano SEGUINTE (Connection) — não o atual. */}
              <UpgradeCard
                planName={t("Plano Connection")}
                desc={t("Quer ainda mais vantagens? Com o Connection você envia notificações push, publica banners no app e ganha destaque no mapa.")}
                cta={t("Fazer upgrade para Connection")}
                onClick={() => navigate("/establishment/subscription")}
              />
              <div style={card}>
                <SectionHdr title={t("Top 10 Idiomas")} />
                {topLanguages.length === 0
                  ? <p style={{ fontSize: 12, color: "#ccc", margin: 0 }}>{t("Sem dados")}</p>
                  : topLanguages.map(l => (
                    <ProgBar key={l.name} label={l.name} value={l.count} max={maxLanguage} />
                  ))
                }
              </div>
            </div>

            {/* Col 3 — Top 10 Assuntos/Interesses + Suporte */}
            <div>
              <div style={{ ...card, position: "relative" }}>
                <SectionHdr title={t("Top 10 Assuntos/Interesses")} onFilter={() => setShowIntFilter(v => !v)} />
                {showIntFilter && (
                  <InterestFilterPopover
                    interests={rawInterests.map(i => i.name)} selected={selInterests}
                    onToggle={handleIntToggle} onApply={() => setShowIntFilter(false)} onClose={() => setShowIntFilter(false)}
                  />
                )}
                {filteredInterests.length === 0
                  ? <p style={{ fontSize: 12, color: "#ccc", margin: 0 }}>{t("Sem dados")}</p>
                  : filteredInterests.slice(0, 10).map(i => (
                    <div key={i.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ color: "#00b6bc" }}><IconStar /></span>
                        <span style={{ fontSize: 12, color: "#333" }}>{i.name}</span>
                      </div>
                      <span style={{ fontSize: 11, color: "#aaa" }}>{t("{count} usuários", { count: i.count })}</span>
                    </div>
                  ))
                }
              </div>
              <div style={card}>
                <p style={{ fontSize: 11, fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: 1, margin: "0 0 10px" }}>{t("Suporte")}</p>
                <a href={`mailto:${support.email}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", background: "#f5f5f5", borderRadius: 8, color: "#333", textDecoration: "none", fontWeight: 600, fontSize: 12, border: "1px solid #e8e8e8" }}><IconMail /> {t("E-mail")}</a>
              </div>
            </div>

          </div>
        </>
      )}

            {/* ══ MEET ══ */}
      {isCconnection && (
        <>
          {/* Stats */}
          <div style={{ ...card, display: "flex", gap: 40, flexWrap: "wrap" }}>
            <StatBlock label={t("Total de check-ins")} value={stats.totalCheckins?.toLocaleString("pt-BR") ?? "—"} trend={10} />
            <StatBlock label={t("Tempo médio de permanência")} value={fmtMins(stats.avgStayMinutes)} trend={10} />
            <StatBlock label={t("Usuários recorrentes")} value={stats.returningUsers?.toLocaleString("pt-BR") ?? "—"} />
          </div>

          {/* Push banner */}
          <div style={{ background: "linear-gradient(135deg, #00b6bc 0%, #007f84 100%)", borderRadius: 14, padding: "18px 22px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <h3 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "#fff" }}>{t("Conecte-se com usuários que fizeram check-in")}</h3>
              <p style={{ margin: 0, fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{t("Envie promoções, novidades e muito mais aos usuários que frequentam seu estabelecimento.")}</p>
            </div>
            <button onClick={() => navigate("/establishment/notification")} style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", background: "#fff", border: "none", borderRadius: 10, color: "#00b6bc", fontWeight: 700, fontSize: 13, cursor: "pointer", flexShrink: 0 }}>
              {t("Disparar Notificação Push")} <IconSend />
            </button>
          </div>

          {/* Tasks */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111" }}>{t("Tarefas")}</h3>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#00b6bc" }}><IconArrow /></button>
            </div>
            <p style={{ fontSize: 11, color: "#aaa", margin: "0 0 10px" }}>
              {t("Cumpra as missões para desbloquear benefícios.")}
            </p>

            {/* Saldo já conquistado, para a recompensa ficar concreta */}
            {missionRewards && missionRewards.pushBonusAvailable > 0 && (
              <div style={{
                display: "inline-block", marginBottom: 10,
                fontSize: 11, fontWeight: 700, color: "#0b6f73",
                background: "#e6f9fa", borderRadius: 999, padding: "4px 10px",
              }}>
                {t("{n} disparo(s) extra(s) disponível(is)", { n: missionRewards.pushBonusAvailable })}
                {missionRewards.pendingDiscount
                  ? ` · ${t("{pct}% de desconto na próxima fatura", { pct: missionRewards.pendingDiscount })}`
                  : ""}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", overflowX: isMobile ? "auto" : "visible" }}>
              {missions.length === 0 ? (
                <p style={{ fontSize: 12, color: "#bbb", margin: 0 }}>
                  {t("Nenhuma missão ativa no momento.")}
                </p>
              ) : (
                missions.map((m) => (
                  <TaskCard
                    key={m.id}
                    title={m.name}
                    sub={descreveRecompensa(m, t)}
                    description={m.description}
                    done={m.completed}
                    current={m.current}
                    target={m.target}
                    percent={m.percent}
                  />
                ))
              )}
            </div>
          </div>

          {/* 3-col */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 240px 220px", gap: 14, alignItems: "start" }}>
            {/* Left */}
            <div>
              <div style={{ position: "relative" }}>
                {isMobile
                  ? <WeeklyChart data={bestDays.map(d => ({ label: d.label, count: d.count }))} />
                  : <PeakChart data={filteredHours} onFilter={() => setShowChartFilter(v => !v)}
                    filterSlot={showChartFilter && (
                      <ChartFilterPopover
                        selectedDay={filterDay} selectedHours={filterHours} availableHours={allHours}
                        onDayChange={setFilterDay} onHourToggle={handleHourToggle}
                        onSelectAllHours={() => setFilterHours([])} onClearHours={() => setFilterHours([])}
                        onApply={() => setShowChartFilter(false)} onClose={() => setShowChartFilter(false)}
                      />
                    )}
                  />}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                <DonutCard title={t("Melhores dias e horários")} data={donutData} onFilter={() => setShowDatePicker(true)} />
                <DonutCard title={t("Estatísticas de retorno do público")} data={returnData} onFilter={() => setShowDatePicker(true)} />
              </div>
            </div>

            {/* Mid */}
            <div>
              <div style={card}>
                <SectionHdr title={t("Top 10 Assuntos/Interesses")} />
                {filteredInterests.slice(0, 10).map(i => (
                  <div key={i.name} style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#00b6bc" }}><IconStar /></span><span style={{ fontSize: 12, color: "#333" }}>{i.name}</span></div>
                    <span style={{ fontSize: 11, color: "#aaa" }}>{t("{count} usuários", { count: i.count })}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: insights */}
            <div>
              {/* Insights do momento — exclusivo MEET */}
              <div style={card}>
                <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#00b6bc" }}>{t("Insights do momento")}</h3>

                {/* Usuários online — check-ins ativos hoje */}
                <div style={{ borderLeft: "3px solid #00b6bc", paddingLeft: 10, marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>{t("Usuários online")}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#111" }}>
                    {activeUsers !== null ? activeUsers.toLocaleString("pt-BR") : "—"}
                  </div>
                </div>

                {/* Número de Meets — in_networking hoje */}
                <div style={{ borderLeft: "3px solid #00b6bc", paddingLeft: 10, marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "#aaa", marginBottom: 2 }}>{t("Número de Meets")}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#111" }}>
                    {activeMeets !== null ? activeMeets.toLocaleString("pt-BR") : "—"}
                  </div>
                </div>

                {/* Top 5 Idiomas */}
                <h4 style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: "#111" }}>{t("Top 5 Idiomas")}</h4>
                {topLanguages.length === 0
                  ? <p style={{ fontSize: 11, color: "#ccc", margin: "0 0 14px" }}>{t("Sem dados")}</p>
                  : topLanguages.slice(0, 5).map(l => (
                    <ProgBar key={l.name} label={l.name} value={l.count} max={maxLanguage} />
                  ))
                }

                {/* Top 5 Assuntos */}
                <h4 style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: "#111" }}>{t("Top 5 Assuntos")}</h4>
                {filteredInterests.length === 0
                  ? <p style={{ fontSize: 11, color: "#ccc" }}>{t("Sem dados")}</p>
                  : filteredInterests.slice(0, 5).map(i => (
                    <ProgBar key={i.name} label={i.name} value={i.count} max={maxInterest} />
                  ))
                }
              </div>
              <div style={card}>
                <p style={{ fontSize: 11, fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: 1, margin: "0 0 10px" }}>{t("Suporte")}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  <a href={`mailto:${support.email}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 10px", background: "#f5f5f5", borderRadius: 8, color: "#333", textDecoration: "none", fontWeight: 600, fontSize: 12, border: "1px solid #e8e8e8" }}><IconMail /> {t("E-mail")}</a>
                  {support.whatsapp && (
                    <a href={`https://wa.me/${support.whatsapp.replace(/\D/g,"")}`} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 10px", background: "#e6f9ef", borderRadius: 8, color: "#1a7a4a", textDecoration: "none", fontWeight: 600, fontSize: 12, border: "1px solid #c6e9d0" }}><IconWA /> WhatsApp</a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};